<template>
  <base-layout>
    <template v-slot:header>
      <nav class="mb-2">
        <router-link
          v-if="category"
          :to="`/services/${category.categorySlug}`"
          class="flex items-center text-sm leading-5 font-medium text-gray-500 hover:text-gray-700"
        >
          <svg class="flex-shrink-0 -ml-1 mr-1 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd"/>
          </svg>
          {{ category.catName }}
        </router-link>
        <router-link
          v-else
          to="/services"
          class="flex items-center text-sm leading-5 font-medium text-gray-500 hover:text-gray-700"
        >
          Back to services
        </router-link>
      </nav>
      <h1 class="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">
        {{ selectedCompany.name || 'Service provider' }}
      </h1>
      <p v-if="category" class="mt-1 text-sm text-gray-600">
        Listed under {{ category.catName }}
      </p>
    </template>

    <template v-slot:main>
      <div v-if="isLoading" class="text-gray-500 py-8">Loading…</div>
      <div v-else-if="loadError" class="py-8">
        <p class="text-gray-700 mb-4">This service provider listing was not found.</p>
        <router-link to="/services" class="text-red-600 hover:underline">Back to services directory</router-link>
      </div>
      <div v-else>
        <CompanyBody
          :isInModal="false"
          :selectedCompanyEnriched="selectedCompany"
          :contactsEnriched="selectedCompanyContacts"
          @openModal="openContactModal"
        />
        <modal-contact ref="contactModal" action="sendMailToContact"></modal-contact>
      </div>
    </template>
  </base-layout>
</template>

<script>
import axios from 'axios'
import { mapActions } from 'vuex'
import BaseLayout from '@/components/BaseLayout.vue'
import CompanyBody from '@/components/CompanyBody.vue'
import ModalContact from '@/components/ModalContact.vue'
import { groupServicesByCategory } from '@/utils/serviceDirectory'
import { useSchemaOrg } from '@/composables/useSchema.org.js'

export default {
  name: 'ServiceCompany',
  components: {
    BaseLayout,
    CompanyBody,
    ModalContact,
  },
  data: () => ({
    isLoading: true,
    loadError: false,
    category: null,
    listing: null,
    selectedCompany: {},
    selectedCompanyContacts: [],
  }),
  async created() {
    await this.load()
  },
  watch: {
    '$route.fullPath'() {
      this.load()
    },
  },
  methods: {
    ...mapActions(['getCompany']),
    async load() {
      this.isLoading = true
      this.loadError = false
      this.category = null
      this.listing = null
      this.selectedCompany = {}
      this.selectedCompanyContacts = []

      try {
        const categorySlug = (this.$route.params.categorySlug || '').toLowerCase()
        const companySlug = (this.$route.params.companySlug || '').toLowerCase()
        const { data } = await axios.get('/api/services')
        const groups = groupServicesByCategory(data.payload || [])
        const category = groups.find(g => g.categorySlug === categorySlug) || null
        const listing = category
          ? category.companies.find(c => c.companySlug === companySlug)
          : null

        if (!category || !listing) {
          this.loadError = true
          document.title = 'Service provider not found - The Broker Site'
          return
        }

        this.category = category
        this.listing = listing

        const resp = await this.getCompany({ id: listing.id, fromServices: true })
        if (!resp || resp.status !== 200 || !resp.company || !resp.company.isService || resp.company.hideServiceDetails) {
          this.loadError = true
          document.title = 'Service provider not found - The Broker Site'
          return
        }

        this.selectedCompany = resp.company
        this.selectedCompanyContacts = resp.contacts || []
        this.applyMeta()
        this.addSchema()
      } catch (e) {
        this.loadError = true
        document.title = 'Service provider not found - The Broker Site'
      } finally {
        this.isLoading = false
      }
    },
    openContactModal(contact) {
      this.$refs.contactModal.openSendMessageModal(contact)
    },
    applyMeta() {
      const companyName = this.selectedCompany.name
      const catName = this.category.catName
      const href = `https://www.thebrokersite.com/services/${this.category.categorySlug}/${this.listing.companySlug}`
      document.title = `${companyName} - ${catName} - The Broker Site`
      this.upsertMeta(
        'description',
        `${companyName} is listed for ${catName} on The Broker Site. View their service provider profile and contact details.`
      )
      this.upsertLink('canonical', href)
    },
    upsertMeta(name, content) {
      let tag = document.querySelector(`meta[name="${name}"]`)
      if (!tag) {
        tag = document.createElement('meta')
        tag.setAttribute('name', name)
        document.head.appendChild(tag)
      }
      tag.setAttribute('content', content)
    },
    upsertLink(rel, href) {
      let tag = document.querySelector(`link[rel="${rel}"]`)
      if (!tag) {
        tag = document.createElement('link')
        tag.setAttribute('rel', rel)
        document.head.appendChild(tag)
      }
      tag.setAttribute('href', href)
    },
    addSchema() {
      useSchemaOrg({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: this.selectedCompany.name,
        url: `https://www.thebrokersite.com/services/${this.category.categorySlug}/${this.listing.companySlug}`,
        address: {
          '@type': 'PostalAddress',
          addressCountry: this.selectedCompany.country,
        },
        makesOffer: {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: this.category.catName,
          },
        },
      })
    },
  },
}
</script>
